import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { candidateService } from '../../services';
import { formatCurrency } from '../../utils/formatters';
import { getProvinces, getDistricts, getAllDistricts } from '../../constants/nepalLocations';

const Candidates = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [province, setProvince] = useState('');
  const [district, setDistrict] = useState('');
  const [constituency, setConstituency] = useState('');
  const [availableDistricts, setAvailableDistricts] = useState(getAllDistricts());
  const [imageErrors, setImageErrors] = useState({});

  const provinces = getProvinces();
  const constituencyOptions = Array.from({ length: 15 }, (_, i) => i + 1); // Nepal has up to 10+ constituencies in some districts like Kathmandu

  useEffect(() => {
    fetchCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, province, district, constituency]);

  // Update available districts when province changes
  useEffect(() => {
    if (province) {
      const provinceDistricts = getDistricts(province);
      setAvailableDistricts(provinceDistricts);
      // If current district is not in the new province's districts, reset it
      if (district && !provinceDistricts.includes(district)) {
        setDistrict('');
      }
    } else {
      setAvailableDistricts(getAllDistricts());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [province]);

  useEffect(() => {
    setAvailableDistricts(getAllDistricts());
  }, []);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const params = {
        limit: 50,
        offset: 0,
        sortBy: 'createdAt',
        order: 'DESC',
      };
      if (search) params.search = search;
      if (province) params.province = province;
      if (district) params.district = district;
      if (constituency) params.constituency = constituency;

      const data = await candidateService.getAllCandidates(params);

      // Handle API response structure: { status, statusCode, data: { items: [], totalCount } }
      const candidatesList = data?.data?.items || [];

      // Fisher-Yates Shuffle
      for (let i = candidatesList.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [candidatesList[i], candidatesList[j]] = [candidatesList[j], candidatesList[i]];
      }

      setCandidates(candidatesList);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      setCandidates([]); // Set to empty array on error
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('candidates.pageTitle')}</h1>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow-md mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder={t('candidates.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent md:col-span-2"
            />
            <select
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white w-full"
            >
              <option value="">All Provinces</option>
              {provinces.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <select
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white w-full"
            >
              <option value="">All Districts</option>
              {availableDistricts.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <select
              value={constituency}
              onChange={(e) => setConstituency(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white w-full"
            >
              <option value="">All Constituencies</option>
              {constituencyOptions.map(num => (
                <option key={num} value={num.toString()}>Constituency {num}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Candidates Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : Array.isArray(candidates) && candidates.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {candidates.map((candidate) => (
              <div key={candidate.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300">
                <div className="h-56 bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center overflow-hidden">
                  {candidate.photoKeyUrl && !imageErrors[candidate.id] ? (
                    <img
                      src={candidate.photoKeyUrl}
                      alt={candidate.name}
                      className="w-full h-full object-cover"
                      onError={() => {
                        setImageErrors(prev => ({ ...prev, [candidate.id]: true }));
                      }}
                    />
                  ) : (
                    <span className="text-5xl font-bold text-primary-600">
                      {candidate.name.charAt(0)}
                    </span>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{candidate.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {candidate.province && `${candidate.province}, `}{candidate.district}, {candidate.constituency}
                  </p>
                  <div className="mb-4">

                  </div>
                  <Link
                    to={`/candidates/${candidate.link || candidate.id}`}
                    className="block w-full text-center bg-primary-600 text-white py-2 rounded-button hover:bg-primary-700 transition"
                  >
                    {t('actions.viewProfile')}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">{t('candidates.noCandidates')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Candidates;
